import React from "react";
import { useNavigate } from "react-router-dom";
import { useContent, useProgress } from "@/lib/queries";
import { greeting, meLabel, daysSince, totalPrompts, stateLabel } from "@/lib/me";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Overline } from "@/components/kit";
import { BookOpen, ArrowRight, Bell } from "lucide-react";

export default function Today() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: content } = useContent();
  const { data: progress } = useProgress();
  const [dismissed, setDismissed] = React.useState(localStorage.getItem("nudge_dismissed") || "");

  if (!content || !progress) return <div className="text-inkfaint text-sm animate-pulse">Loading…</div>;

  const cur = content.states[progress.current_month] || content.states[0];
  const curMonth = progress.months.find((m) => m.id === cur.id) || { answered_count: 0 };
  const total = totalPrompts(cur);

  // nudge
  let nudge = null;
  const N = progress.last_completed_id;
  if (N != null && N < 11) {
    const d = daysSince(progress.last_completed_at);
    if (d >= 30) nudge = { key: `ready-${N}`, title: "A gentle nudge", msg: `Are you ready for ${meLabel(N + 1)}?`, to: `/month/${N + 1}` };
    else if (d >= 25) nudge = { key: `review-${N}`, title: "Time to review", msg: `Review ${meLabel(N)}: return to what happened this month.`, to: `/journal/${N}` };
  }
  if (nudge && dismissed === nudge.key) nudge = null;

  const heroOverline = cur.id === 0 ? `${cur.me} · Baseline` : `${cur.me} · Month ${cur.id} of 11`;

  return (
    <div className="space-y-8 em-fade-up" data-testid="today-page">
      <div>
        <Overline>ME₀ → ME₁₁ · The Emergence Journal</Overline>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-3">
          {greeting()}, {user?.name?.split(" ")[0] || "Traveller"}
        </h1>
      </div>

      {nudge && (
        <Card className="flex items-start gap-4 border-goldmuted/40" data-testid="nudge-card">
          <Bell className="w-5 h-5 text-gold mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-ink font-semibold">{nudge.title}</div>
            <p className="text-inkfaint text-sm mt-1">{nudge.msg}</p>
            <div className="flex gap-4 mt-3">
              <button onClick={() => navigate(nudge.to)} className="text-gold text-sm hover:underline" data-testid="nudge-go">Take me there</button>
              <button
                onClick={() => { localStorage.setItem("nudge_dismissed", nudge.key); setDismissed(nudge.key); }}
                className="text-faint text-sm hover:text-inkfaint" data-testid="nudge-dismiss">Dismiss</button>
            </div>
          </div>
        </Card>
      )}

      {/* Hero */}
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.12]" style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1609083590460-7b8cc0ca65f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
          backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative">
          <Overline>{heroOverline}</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-3">{cur.theme}</h2>
          <p className="text-inksoft mt-3 max-w-xl leading-relaxed">{cur.enquiry}</p>
          <div className="mt-5 text-sm text-inkfaint">{curMonth.answered_count}/{total} answered</div>
          <div className="mt-6">
            <Btn onClick={() => navigate(`/month/${cur.id}`)} data-testid="open-month-btn">
              {curMonth.answered_count > 0 ? "Continue this month" : "Open this month"}
              <ArrowRight className="w-4 h-4" />
            </Btn>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="text-center">
          <div className="font-serif text-4xl text-gold">{progress.months_visited}</div>
          <div className="text-xs text-faint mt-1">{progress.months_visited === 1 ? "month active" : "months active"}</div>
        </Card>
        <Card className="text-center">
          <div className="font-serif text-4xl text-gold">{progress.total_completed}</div>
          <div className="text-xs text-faint mt-1">states complete</div>
        </Card>
      </div>

      {/* Framework link */}
      <Card className="flex items-center gap-4 cursor-pointer hover:border-goldmuted/40 transition-colors"
        onClick={() => navigate("/learn")} data-testid="framework-card">
        <BookOpen className="w-6 h-6 text-gold shrink-0" />
        <div className="flex-1">
          <Overline>New to this?</Overline>
          <div className="text-ink font-semibold mt-1">Read the framework first</div>
          <p className="text-inkfaint text-sm mt-1">Human Architecture, the 12 + 1, and the monthly cycle.</p>
        </div>
        <ArrowRight className="w-5 h-5 text-inkfaint" />
      </Card>
    </div>
  );
}
